import React, { CSSProperties, useEffect, useRef } from 'react';
import * as THREE from 'three';

export interface SignalDotConfig {
  /** Number of signal dots to display */
  count?: number;
  /** Base bead radius in world units */
  size?: number;
  /** Size variation (0-1, multiplied by base size) */
  sizeVariation?: number;
  /** Base movement speed */
  speed?: number;
  /** Speed variation (0-1, multiplied by base speed) */
  speedVariation?: number;
  /** Visual opacity of the beads (0-1) */
  opacity?: number;
  /** Whether dots can move in X direction */
  moveX?: boolean;
  /** Whether dots can move in Z direction */
  moveZ?: boolean;
  /** Probability of reversing direction (0-1) */
  reverseChance?: number;
  /** Radius of the dip in the mesh beneath each dot (0 = no dip) */
  dipRadius?: number;
  /** Depth of the dip in the mesh beneath each dot */
  dipDepth?: number;
  /** Colors for the dots (CSS color strings) */
  colors?: string[];
}

export interface FunctionMeshHeroProps {
  /** Width of the mesh plane in world units */
  width?: number;
  /** Depth of the mesh plane in world units */
  depth?: number;
  /** Number of lines along the X axis */
  xLines?: number;
  /** Number of lines along the Z axis */
  zLines?: number;

  /** Wave amplitude - controls the height of oscillations */
  amplitude?: number;
  /** Animation speed multiplier */
  speed?: number;
  /** Frequency of waves along X axis */
  freqX?: number;
  /** Frequency of waves along Z axis */
  freqZ?: number;
  /** Height of the ridge peaks */
  ridgeHeight?: number;
  /** How much the ridges drift over time */
  ridgeDrift?: number;
  /** Lift applied to the back of the mesh */
  backLift?: number;

  /** Background color (CSS color string) */
  background?: string;
  /** Line color (CSS color string) */
  lineColor?: string;
  /** Opacity of horizontal (row) lines */
  rowOpacity?: number;
  /** Opacity of vertical (column) lines */
  colOpacity?: number;

  /** Camera X position */
  cameraX?: number;
  /** Camera Y position */
  cameraY?: number;
  /** Camera Z position */
  cameraZ?: number;
  /** Camera look-at X */
  lookX?: number;
  /** Camera look-at Y */
  lookY?: number;
  /** Camera look-at Z */
  lookZ?: number;
  /** Camera field of view */
  fov?: number;

  /**
   * Deprecated: no longer used.
   * Kept only so existing usage does not break.
   */
  dotBackground?: string;

  /** Vertical offset of the entire mesh */
  meshOffsetY?: number;
  /** Yaw rotation of the mesh (radians) */
  meshYaw?: number;

  /** Signal dots configuration */
  signalDots?: SignalDotConfig;

  /** Additional CSS class names */
  className?: string;
  /** Inline styles */
  style?: CSSProperties;
}

const defaultSignalDots: Required<SignalDotConfig> = {
  count: 12,
  size: 0.08,
  sizeVariation: 0.4,
  speed: 0.3,
  speedVariation: 0.5,
  opacity: 0.9,
  moveX: true,
  moveZ: true,
  reverseChance: 0.5,
  dipRadius: 0.8,
  dipDepth: 0.15,
  colors: ['#4ADE80', '#E88BD5', '#F5A623'],
};

const defaults: Required<Omit<FunctionMeshHeroProps, 'className' | 'style' | 'signalDots'>> = {
  width: 40,
  depth: 20,
  xLines: 120,
  zLines: 54,

  amplitude: 1.28,
  speed: 0.85,
  freqX: 0.18,
  freqZ: 0.56,
  ridgeHeight: 2.85,
  ridgeDrift: 0.32,
  backLift: 1.25,

  background: '#ece8df',
  lineColor: '#252525',
  rowOpacity: 0.22,
  colOpacity: 0.15,

  cameraX: 0.0,
  cameraY: 4.1,
  cameraZ: 20.6,
  lookX: 0.9,
  lookY: 1.55,
  lookZ: 6.6,
  fov: 31,

  meshOffsetY: -1.95,
  meshYaw: -0.16,
  dotBackground: '#0f0f0f',
};

type MergedProps = Required<Omit<FunctionMeshHeroProps, 'className' | 'style' | 'signalDots'>>;
type MergedSignalDots = Required<SignalDotConfig>;

interface SignalDot {
  x: number;
  z: number;
  vx: number;
  vz: number;
  size: number;
  group: THREE.Group;
}

function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}

function gaussian(x: number, z: number, cx: number, cz: number, sx: number, sz: number): number {
  const dx = x - cx;
  const dz = z - cz;
  return Math.exp(-((dx * dx) / (2 * sx * sx) + (dz * dz) / (2 * sz * sz)));
}

function surface(x: number, z: number, t: number, p: MergedProps): number {
  const phase = t * p.speed;
  const drift = Math.sin(phase * 0.38) * p.ridgeDrift;

  let y = 0;
  y += Math.sin(x * p.freqX + phase) * p.amplitude * 0.54;
  y += Math.sin(x * p.freqX * 2.35 + z * 0.14 - phase * 0.75) * p.amplitude * 0.22;
  y += Math.sin(-z * p.freqZ + x * 0.13 + phase * 0.42) * p.amplitude * 0.34;
  y += Math.sin((x + z * 0.78) * 0.15 - phase * 0.24) * p.amplitude * 0.16;

  y += gaussian(x, z, 1.8 - drift * 2.4, 6.4, 4.7, 1.9) * p.ridgeHeight;
  y += gaussian(x, z, 8.8 + drift * 1.7, 5.1, 2.9, 1.1) * p.ridgeHeight * 0.76;
  y -= gaussian(x, z, 5.4, 8.2, 2.1, 0.92) * p.ridgeHeight * 0.44;
  y += gaussian(x, z, -11.4, 5.2, 5.4, 1.8) * p.ridgeHeight * 0.28;
  y += (1 - z / p.depth) * p.backLift;

  return y;
}

export default function FunctionMeshHero({
  className,
  style,
  signalDots: signalDotsConfig,
  ...rawProps
}: FunctionMeshHeroProps) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const paramsKey = JSON.stringify({ ...defaults, ...rawProps, signalDots: signalDotsConfig });

  useEffect(() => {
    const host = mountRef.current;
    if (!host) return;

    const p: MergedProps = {
      ...defaults,
      ...rawProps,
      xLines: Math.max(2, Math.floor(rawProps.xLines ?? defaults.xLines)),
      zLines: Math.max(2, Math.floor(rawProps.zLines ?? defaults.zLines)),
    };

    const dotConfig: MergedSignalDots = {
      ...defaultSignalDots,
      ...signalDotsConfig,
      count: Math.max(0, Math.floor(signalDotsConfig?.count ?? defaultSignalDots.count)),
      colors: signalDotsConfig?.colors ?? defaultSignalDots.colors,
      opacity: THREE.MathUtils.clamp(signalDotsConfig?.opacity ?? defaultSignalDots.opacity, 0, 1),
    };

    const isTransparentBackground = p.background === 'transparent';

    const scene = new THREE.Scene();
    if (!isTransparentBackground) {
      scene.background = new THREE.Color(p.background);
    }

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: isTransparentBackground,
      powerPreference: 'high-performance',
    });

    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setClearColor(
      isTransparentBackground ? 0x000000 : new THREE.Color(p.background),
      isTransparentBackground ? 0 : 1,
    );
    // resize() sets only the drawing buffer, at the pixel ratio; the canvas fills the host in
    // CSS, or a 2x screen would show it at twice the host's size, mostly clipped.
    renderer.domElement.style.display = 'block';
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    host.appendChild(renderer.domElement);

    const camera = new THREE.PerspectiveCamera(p.fov, 1, 0.1, 100);
    camera.position.set(p.cameraX, p.cameraY, p.cameraZ);
    camera.lookAt(p.lookX, p.lookY, p.lookZ);
    camera.updateProjectionMatrix();

    const root = new THREE.Group();
    root.position.y = p.meshOffsetY;
    root.rotation.y = p.meshYaw;
    root.updateMatrix();
    root.matrixAutoUpdate = false;
    scene.add(root);

    const rowMaterial = new THREE.LineBasicMaterial({
      color: p.lineColor,
      transparent: true,
      opacity: p.rowOpacity,
      depthWrite: false,
      depthTest: true,
      fog: false,
    });

    const colMaterial = new THREE.LineBasicMaterial({
      color: p.lineColor,
      transparent: true,
      opacity: p.colOpacity,
      depthWrite: false,
      depthTest: true,
      fog: false,
    });

    const halfWidth = p.width / 2;
    const xStep = p.width / (p.xLines - 1);
    const zStep = p.depth / (p.zLines - 1);

    const xCoords = new Float32Array(p.xLines);
    const zCoords = new Float32Array(p.zLines);
    const heights = new Float32Array(p.xLines * p.zLines);

    for (let xi = 0; xi < p.xLines; xi += 1) {
      xCoords[xi] = -halfWidth + xi * xStep;
    }

    for (let zi = 0; zi < p.zLines; zi += 1) {
      zCoords[zi] = zi * zStep;
    }

    const rowPositions = new Float32Array(p.zLines * (p.xLines - 1) * 2 * 3);
    const rowGeometry = new THREE.BufferGeometry();
    const rowAttribute = new THREE.BufferAttribute(rowPositions, 3);
    rowAttribute.setUsage(THREE.DynamicDrawUsage);
    rowGeometry.setAttribute('position', rowAttribute);

    const rowSegments = new THREE.LineSegments(rowGeometry, rowMaterial);
    rowSegments.frustumCulled = false;
    rowSegments.renderOrder = 2;
    root.add(rowSegments);

    const colPositions = new Float32Array(p.xLines * (p.zLines - 1) * 2 * 3);
    const colGeometry = new THREE.BufferGeometry();
    const colAttribute = new THREE.BufferAttribute(colPositions, 3);
    colAttribute.setUsage(THREE.DynamicDrawUsage);
    colGeometry.setAttribute('position', colAttribute);

    const colSegments = new THREE.LineSegments(colGeometry, colMaterial);
    colSegments.frustumCulled = false;
    colSegments.renderOrder = 2;
    root.add(colSegments);

    const beadGeometry = new THREE.SphereGeometry(1, 14, 14);

    const beadDepthMaterial = new THREE.MeshBasicMaterial({ fog: false });
    beadDepthMaterial.colorWrite = false;
    beadDepthMaterial.depthWrite = true;
    beadDepthMaterial.depthTest = true;

    const beadColorMaterials = dotConfig.colors.map((colorStr) => {
      const material = new THREE.MeshBasicMaterial({
        color: colorStr,
        transparent: true,
        opacity: dotConfig.opacity,
        depthTest: true,
        depthWrite: false,
        fog: false,
      });

      return material;
    });

    const signalDots: SignalDot[] = [];
    const rand = seededRandom(42);

    if (dotConfig.count > 0 && beadColorMaterials.length > 0) {
      for (let i = 0; i < dotConfig.count; i += 1) {
        const colorIndex = i % beadColorMaterials.length;

        const sizeVar = 1 - dotConfig.sizeVariation + rand() * dotConfig.sizeVariation * 2;
        const dotSize = dotConfig.size * sizeVar;

        const speedVar = 1 - dotConfig.speedVariation + rand() * dotConfig.speedVariation * 2;
        const baseSpeed = dotConfig.speed * speedVar;

        const x = -halfWidth + rand() * p.width;
        const z = rand() * p.depth;

        let vx = 0;
        let vz = 0;

        if (dotConfig.moveX && dotConfig.moveZ) {
          const angle = rand() * Math.PI * 2;
          vx = Math.cos(angle) * baseSpeed;
          vz = Math.sin(angle) * baseSpeed;
        } else if (dotConfig.moveX) {
          vx = baseSpeed * (rand() > dotConfig.reverseChance ? 1 : -1);
        } else if (dotConfig.moveZ) {
          vz = baseSpeed * (rand() > dotConfig.reverseChance ? 1 : -1);
        }

        const group = new THREE.Group();

        const depthMesh = new THREE.Mesh(beadGeometry, beadDepthMaterial);
        depthMesh.frustumCulled = false;
        depthMesh.renderOrder = 0;

        const colorMesh = new THREE.Mesh(beadGeometry, beadColorMaterials[colorIndex]);
        colorMesh.frustumCulled = false;
        colorMesh.renderOrder = 1;

        group.add(depthMesh, colorMesh);
        group.scale.setScalar(dotSize);
        root.add(group);

        signalDots.push({
          x,
          z,
          vx,
          vz,
          size: dotSize,
          group,
        });
      }
    }

    const dipRadiusSq = dotConfig.dipRadius * dotConfig.dipRadius;

    const calculateDipAt = (x: number, z: number): number => {
      if (signalDots.length === 0 || dotConfig.dipDepth <= 0 || dotConfig.dipRadius <= 0) {
        return 0;
      }

      let totalDip = 0;
      for (let i = 0; i < signalDots.length; i += 1) {
        const dot = signalDots[i];
        const dx = x - dot.x;
        const dz = z - dot.z;
        const distSq = dx * dx + dz * dz;

        if (distSq >= dipRadiusSq) continue;

        const t = Math.sqrt(distSq) / dotConfig.dipRadius;
        const falloff = 0.5 * (1 + Math.cos(t * Math.PI));
        totalDip -= dotConfig.dipDepth * falloff;
      }

      return totalDip;
    };

    const sampleSurface = (x: number, z: number, t: number): number => {
      return surface(x, z, t, p) + calculateDipAt(x, z);
    };

    const normalSample = Math.max(0.04, Math.min(xStep, zStep) * 0.75);
    const tangentX = new THREE.Vector3();
    const tangentZ = new THREE.Vector3();
    const surfaceNormal = new THREE.Vector3();

    const getSurfaceNormal = (x: number, z: number, t: number, out: THREE.Vector3): THREE.Vector3 => {
      const yL = sampleSurface(x - normalSample, z, t);
      const yR = sampleSurface(x + normalSample, z, t);
      const yD = sampleSurface(x, z - normalSample, t);
      const yU = sampleSurface(x, z + normalSample, t);

      tangentX.set(normalSample * 2, yR - yL, 0);
      tangentZ.set(0, yU - yD, normalSample * 2);

      out.copy(tangentZ).cross(tangentX).normalize();

      if (out.y < 0) out.multiplyScalar(-1);

      return out;
    };

    const advanceDots = (dt: number) => {
      if (signalDots.length === 0) return;

      for (let i = 0; i < signalDots.length; i += 1) {
        const dot = signalDots[i];
        dot.x += dot.vx * dt;
        dot.z += dot.vz * dt;

        if (dot.x < -halfWidth) {
          dot.x += p.width;
        } else if (dot.x > halfWidth) {
          dot.x -= p.width;
        }

        if (dot.z < 0) {
          dot.z += p.depth;
        } else if (dot.z > p.depth) {
          dot.z -= p.depth;
        }
      }
    };

    const rebuildHeightField = (t: number) => {
      let index = 0;

      for (let zi = 0; zi < p.zLines; zi += 1) {
        const z = zCoords[zi];
        for (let xi = 0; xi < p.xLines; xi += 1) {
          const x = xCoords[xi];
          heights[index] = sampleSurface(x, z, t);
          index += 1;
        }
      }
    };

    const rebuildRowSegments = () => {
      let ptr = 0;

      for (let zi = 0; zi < p.zLines; zi += 1) {
        const z = zCoords[zi];
        const rowBase = zi * p.xLines;

        for (let xi = 0; xi < p.xLines - 1; xi += 1) {
          const i0 = rowBase + xi;
          const i1 = i0 + 1;

          rowPositions[ptr++] = xCoords[xi];
          rowPositions[ptr++] = heights[i0];
          rowPositions[ptr++] = z;

          rowPositions[ptr++] = xCoords[xi + 1];
          rowPositions[ptr++] = heights[i1];
          rowPositions[ptr++] = z;
        }
      }

      rowAttribute.needsUpdate = true;
    };

    const rebuildColSegments = () => {
      let ptr = 0;

      for (let xi = 0; xi < p.xLines; xi += 1) {
        const x = xCoords[xi];

        for (let zi = 0; zi < p.zLines - 1; zi += 1) {
          const i0 = zi * p.xLines + xi;
          const i1 = i0 + p.xLines;

          colPositions[ptr++] = x;
          colPositions[ptr++] = heights[i0];
          colPositions[ptr++] = zCoords[zi];

          colPositions[ptr++] = x;
          colPositions[ptr++] = heights[i1];
          colPositions[ptr++] = zCoords[zi + 1];
        }
      }

      colAttribute.needsUpdate = true;
    };

    const updateDots = (t: number) => {
      if (signalDots.length === 0) return;

      for (let i = 0; i < signalDots.length; i += 1) {
        const dot = signalDots[i];
        const y = sampleSurface(dot.x, dot.z, t);

        getSurfaceNormal(dot.x, dot.z, t, surfaceNormal);

        const lift = dot.size * 0.82 + Math.max(0.006, dot.size * 0.05);

        dot.group.position.set(
          dot.x + surfaceNormal.x * lift,
          y + surfaceNormal.y * lift,
          dot.z + surfaceNormal.z * lift,
        );
      }
    };

    const drawFrame = (t: number, dt: number) => {
      advanceDots(dt);
      rebuildHeightField(t);
      rebuildRowSegments();
      rebuildColSegments();
      updateDots(t);
      renderer.render(scene, camera);
    };

    const resize = () => {
      const width = Math.max(1, host.clientWidth);
      const height = Math.max(1, host.clientHeight);

      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(host);

    resize();
    drawFrame(0, 0);

    // three.js checks on shader compilation from a timer, and throws if the materials are
    // disposed before it finishes, so the cleanup below waits for this to settle.
    const compiled = 'compileAsync' in renderer
      ? renderer.compileAsync(scene, camera).catch(() => undefined)
      : Promise.resolve();

    const clock = new THREE.Clock();
    let rafId = 0;
    let active = false;
    let inViewport = true;
    let pageVisible = !document.hidden;
    let lastTime = 0;

    const tick = () => {
      if (!active) return;

      rafId = window.requestAnimationFrame(tick);

      const t = clock.getElapsedTime();
      const dt = Math.min(t - lastTime, 0.05);
      lastTime = t;

      drawFrame(t, dt);
    };

    const setActive = (next: boolean) => {
      if (active === next) return;
      active = next;

      if (active) {
        lastTime = clock.getElapsedTime();
        tick();
      } else if (rafId !== 0) {
        window.cancelAnimationFrame(rafId);
        rafId = 0;
      }
    };

    const syncActivity = () => {
      setActive(inViewport && pageVisible);
    };

    const onVisibilityChange = () => {
      pageVisible = !document.hidden;
      syncActivity();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    let intersectionObserver: IntersectionObserver | null = null;
    if (typeof IntersectionObserver !== 'undefined') {
      intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          inViewport = entry?.isIntersecting ?? true;
          syncActivity();
        },
        { threshold: 0 },
      );

      intersectionObserver.observe(host);
    }

    syncActivity();

    return () => {
      setActive(false);
      resizeObserver.disconnect();
      intersectionObserver?.disconnect();
      document.removeEventListener('visibilitychange', onVisibilityChange);

      if (renderer.domElement.parentNode === host) {
        host.removeChild(renderer.domElement);
      }

      void compiled.then(() => {
        rowGeometry.dispose();
        colGeometry.dispose();
        rowMaterial.dispose();
        colMaterial.dispose();

        beadGeometry.dispose();
        beadDepthMaterial.dispose();
        beadColorMaterials.forEach((material) => material.dispose());

        renderer.dispose();
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  return (
    <div
      ref={mountRef}
      className={className}
      style={{
        width: '100%',
        height: '100%',
        ...style,
      }}
    />
  );
}

export { FunctionMeshHero };
